import {
  ASRProviderReferenceSuiteOutput,
  ASRProviderReferenceSuiteOverlay,
  ASRProviderReferenceSuiteStatus,
  ConfluenceSourceParametersLoadingMode,
  IngestionPipelineOutput,
  IngestionSourceExtractionOutput,
  IngestionSourceParametersOutput,
  IngestionSourceParametersType,
  OCRProviderReferenceSuiteOutput,
  OCRProviderReferenceSuiteOverlay,
  OCRProviderReferenceSuiteStatus,
  WebsiteSourceParametersReceivingMode,
} from '../../../../../../gamma-api';
import { generateExternalId } from '../../utils';
import { DEFAULT_ELMA365_FILTRATION_JSON } from '../SourceSettings/ELMA365AppFieldsSelector/utils';

import { CONFLUENCE_DEFAULT_CONTENT_SELECTOR, CONFLUENCE_DEFAULT_FILTER_SELECTOR } from './const';

export const getSourceOcrOverlays = (pipeline?: IngestionPipelineOutput): OCRProviderReferenceSuiteOverlay[] => {
  if (pipeline?.ocr?.status !== OCRProviderReferenceSuiteStatus.Enabled) {
    return [];
  }

  return [OCRProviderReferenceSuiteOverlay.Inherits, OCRProviderReferenceSuiteOverlay.Overrides];
};

export const getSourceOcrSettings = (
  pipeline?: IngestionPipelineOutput,
  extraction?: IngestionSourceExtractionOutput
): OCRProviderReferenceSuiteOutput | undefined | null => {
  if (extraction?.ocr?.status === OCRProviderReferenceSuiteStatus.Disabled) {
    return extraction.ocr;
  }

  if (extraction?.ocr?.overlay === OCRProviderReferenceSuiteOverlay.Overrides) {
    return extraction.ocr;
  }

  if (!pipeline?.ocr) {
    return null;
  }

  return {
    ...pipeline.ocr,
    status: extraction?.ocr?.status || pipeline.ocr.status,
    overlay:
      pipeline.ocr.status !== OCRProviderReferenceSuiteStatus.Enabled
        ? OCRProviderReferenceSuiteOverlay.Overrides
        : OCRProviderReferenceSuiteOverlay.Inherits,
  };
};

export const mapSourceOcrSettings = (
  oldValue?: OCRProviderReferenceSuiteOutput | null,
  newValue?: OCRProviderReferenceSuiteOutput | null
): OCRProviderReferenceSuiteOutput | null => {
  if (!oldValue || !newValue) {
    return newValue || null;
  }

  if (oldValue.status === newValue.status && oldValue.overlay === newValue.overlay) {
    return newValue;
  }

  return { ...oldValue, status: newValue.status, overlay: newValue.overlay };
};

export const getSourceAsrOverlays = (pipeline?: IngestionPipelineOutput): ASRProviderReferenceSuiteOverlay[] => {
  if (pipeline?.asr?.status !== ASRProviderReferenceSuiteStatus.Enabled) {
    return [];
  }

  return [ASRProviderReferenceSuiteOverlay.Inherits, ASRProviderReferenceSuiteOverlay.Overrides];
};

export const getSourceAsrSettings = (
  pipeline?: IngestionPipelineOutput,
  extraction?: IngestionSourceExtractionOutput
): ASRProviderReferenceSuiteOutput | undefined | null => {
  if (extraction?.asr?.status === ASRProviderReferenceSuiteStatus.Disabled) {
    return extraction.asr;
  }

  if (extraction?.asr?.overlay === ASRProviderReferenceSuiteOverlay.Overrides) {
    return extraction.asr;
  }

  if (!pipeline?.asr) {
    return null;
  }

  return {
    ...pipeline.asr,
    status: extraction?.asr?.status || pipeline.asr.status,
    overlay:
      pipeline.asr.status !== ASRProviderReferenceSuiteStatus.Enabled
        ? ASRProviderReferenceSuiteOverlay.Overrides
        : ASRProviderReferenceSuiteOverlay.Inherits,
  };
};

export const mapSourceAsrSettings = (
  oldValue?: ASRProviderReferenceSuiteOutput | null,
  newValue?: ASRProviderReferenceSuiteOutput | null
): ASRProviderReferenceSuiteOutput | null => {
  if (!oldValue || !newValue) {
    return newValue || null;
  }

  if (oldValue.status === newValue.status && oldValue.overlay === newValue.overlay) {
    return newValue;
  }

  return { ...oldValue, status: newValue.status, overlay: newValue.overlay };
};

export const createParametersForType = (type: IngestionSourceParametersType): IngestionSourceParametersOutput => {
  switch (type) {
    case IngestionSourceParametersType.Website:
      return {
        external_id: generateExternalId(),
        type: IngestionSourceParametersType.Website,
        name: '',
        settings: {
          source_type: IngestionSourceParametersType.Website,
          base_url: '',
          extra_urls: [],
          receiving_mode: WebsiteSourceParametersReceivingMode.Crawler,
        },
        extraction: {
          file: {},
          html: {
            content_selector: '',
            filter_selector: '',
          },
          text: {},
          ocr: null,
          asr: null,
        },
      };
    case IngestionSourceParametersType.File:
      return {
        external_id: generateExternalId(),
        type: IngestionSourceParametersType.File,
        name: '',
        settings: {
          source_type: IngestionSourceParametersType.File,
          files: [],
        },
        extraction: {
          file: {
            converter_engine: null,
            conversion_format: null,
          },
          html: {},
          text: {},
          ocr: null,
          asr: null,
        },
      };
    case IngestionSourceParametersType.Elma365:
      return {
        external_id: generateExternalId(),
        type: IngestionSourceParametersType.Elma365,
        name: '',
        settings: {
          source_type: IngestionSourceParametersType.Elma365,
          connector_id: '',
          filtration_enabled: false,
          filtration_json: DEFAULT_ELMA365_FILTRATION_JSON,
          feed: {
            enabled: false,
            files: {
              enabled: false,
            },
          },
          status: {
            refresh_enabled: false,
            selected_item_ids: [],
          },
          files: {
            enabled: false,
          },
        },
        extraction: {
          file: {},
          html: {},
          text: {},
          ocr: null,
          asr: null,
        },
      };
    case IngestionSourceParametersType.Confluence:
    default:
      return {
        external_id: generateExternalId(),
        type: IngestionSourceParametersType.Confluence,
        name: '',
        settings: {
          source_type: IngestionSourceParametersType.Confluence,
          base_url: '',
          loading_mode: ConfluenceSourceParametersLoadingMode.Pages,
        },
        extraction: {
          file: {},
          html: {
            content_selector: CONFLUENCE_DEFAULT_CONTENT_SELECTOR,
            filter_selector: CONFLUENCE_DEFAULT_FILTER_SELECTOR,
          },
          text: {},
          ocr: null,
          asr: null,
        },
      };
  }
};
